import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MortgageSlider } from '@/components/calculator/MortgageSlider'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { calculateMortgage } from '@/lib/mortgage'
import type { MortgageInput, InstallmentType } from '@/lib/mortgage'
import { LIMITS, formatPLN, formatPercent } from '@/lib/constants'
import { loadLanguage } from '@/i18n/i18n'

const ATTR_MAX_LENGTH = 60
const SUPPORTED_LANGS = ['pl', 'en', 'fr', 'es', 'de', 'ru', 'uk'] as const
type SupportedLang = (typeof SUPPORTED_LANGS)[number]

/**
 * Strip control characters and tags from the user-supplied attribution prefix,
 * then trim and cap length. React already escapes when rendering as text — this
 * is belt-and-suspenders to keep attribution looking like a normal short label
 * rather than something pretending to be HTML/markup.
 */
function sanitizeAttribution(raw: string | null): string {
  if (!raw) return ''
  return raw
    .slice(0, ATTR_MAX_LENGTH)
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x7F<>]/g, '')
    .trim()
}

/**
 * Sensible locale per currency for `Intl.NumberFormat`. Currency formatting
 * conventions vary (e.g. "€500.000" in DE vs "500 000 €" in FR vs "€500,000"
 * in en-US). Picking a locale that matches the currency's home country keeps
 * grouping and symbol placement natural. Unknown currencies fall through to
 * `en-US` — neutral and widely-readable.
 */
const CURRENCY_LOCALES: Record<string, string> = {
  PLN: 'pl-PL',
  EUR: 'de-DE',
  USD: 'en-US',
  GBP: 'en-GB',
  CHF: 'de-CH',
  CZK: 'cs-CZ',
  HUF: 'hu-HU',
  RON: 'ro-RO',
  RUB: 'ru-RU',
  UAH: 'uk-UA',
  SEK: 'sv-SE',
  NOK: 'nb-NO',
  DKK: 'da-DK',
  JPY: 'ja-JP',
  CAD: 'en-CA',
  AUD: 'en-AU',
}

/** Validate the `?currency=` param. Only accept 3-letter ISO 4217-style codes. */
function sanitizeCurrency(raw: string | null): string {
  if (!raw) return 'PLN'
  if (!/^[A-Za-z]{3}$/.test(raw)) return 'PLN'
  return raw.toUpperCase()
}

/**
 * Build a currency formatter. `Intl.NumberFormat` doesn't throw for unknown
 * currency codes — it just renders the code itself (e.g. "ZZZ 500,000"). That
 * fallback is acceptable, so we don't try/catch.
 */
function makeCurrencyFormatter(currency: string): (amount: number) => string {
  if (currency === 'PLN') return formatPLN
  const locale = CURRENCY_LOCALES[currency] ?? 'en-US'
  const fmt = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  return (amount) => fmt.format(amount)
}

const ANNUAL_RATE_LIMITS = { min: 1, max: 20, step: 0.05 }

const EMBED_DEFAULTS = {
  propertyPrice: 500_000,
  downPaymentPercent: 20,
  loanYears: 25,
  annualRate: 7.85,
}

const CTA_HREF = 'https://ratygo.pl/?utm_source=embed&utm_medium=widget&utm_campaign=embed_cta'
const BRAND_HREF = 'https://ratygo.pl/?utm_source=embed&utm_medium=widget&utm_campaign=embed_brand'

const HouseIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
  </svg>
)

const PercentIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
)

const ChartIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181" />
  </svg>
)

/**
 * Standalone, embeddable calculator widget rendered without site chrome.
 *
 * Loaded inside <iframe> on third-party Polish finance/property blogs as a
 * lead-gen surface — every embed = a backlink + referral traffic to RatyGo.
 * Kept deliberately minimal (4 sliders, single result, one CTA) so it loads
 * fast and fits in typical blog content widths (~600-800px). The full
 * calculator with overpayment/rent-vs-buy/scenarios lives on the homepage.
 *
 * Layout adapts via *container queries* (`@container` + `@xs:`/`@sm:`/`@md:`)
 * rather than viewport queries — because the iframe IS the viewport, but the
 * iframe can be sized however the embedding site wants (narrow sidebar, full
 * width, etc). Container queries match the actual rendered size of the widget,
 * not the device screen.
 *
 * Breakpoints (Tailwind v4 defaults):
 *   `@xs` 20rem / 320px — secondary results switch from stack to 3-col
 *   `@sm` 24rem / 384px — full padding, full CTA text
 *
 * URL params (let embedders customize per-site without code changes):
 *   `?lang=pl|en|fr|es|de|ru|uk` — UI language. Default: pl. Unknown values are ignored.
 *   `?attr=<text>` — attribution prefix shown before the "RatyGo.pl" link.
 *     Sanitized to plain text, capped at 60 chars. The "RatyGo.pl" link is
 *     ALWAYS appended — it cannot be removed via this param. This lets a
 *     blogger in any language localize the attribution (e.g. `?attr=Calculatrice
 *     fournie par`) without us needing translation files for that language.
 *   `?currency=<ISO>` — currency symbol/format. Default: PLN. Accepts any
 *     ISO 4217 3-letter code (EUR, USD, GBP, etc.). Only changes how values
 *     are rendered — the underlying numbers, interest rates and limits stay
 *     in the Polish-market range. Embedders in non-PL markets should adjust
 *     the rate slider to their local typical (e.g. ~3.5% for EUR markets).
 *
 * `noindex` on this route so the iframe URL itself doesn't compete with the
 * homepage in search results — parent pages get the ranking credit.
 */
export function EmbedPage() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()

  const customAttr = useMemo(() => sanitizeAttribution(searchParams.get('attr')), [searchParams])
  const langParam = searchParams.get('lang')
  const currency = useMemo(() => sanitizeCurrency(searchParams.get('currency')), [searchParams])
  const formatMoney = useMemo(() => makeCurrencyFormatter(currency), [currency])

  // Switch i18n language based on ?lang param. Non-default languages are
  // lazy-loaded — for missing keys, i18next falls back to pl (intentional, the
  // non-pl/en locales only translate the embed-relevant subset of keys).
  // Unknown languages silently fall through to the default (pl).
  useEffect(() => {
    if (!langParam || !SUPPORTED_LANGS.includes(langParam as SupportedLang)) return
    if (i18n.language === langParam) return
    loadLanguage(langParam).then(() => i18n.changeLanguage(langParam))
  }, [langParam, i18n])

  const [propertyPrice, setPropertyPrice] = useState(EMBED_DEFAULTS.propertyPrice)
  const [downPaymentPercent, setDownPaymentPercent] = useState(EMBED_DEFAULTS.downPaymentPercent)
  const [loanYears, setLoanYears] = useState(EMBED_DEFAULTS.loanYears)
  const [annualRate, setAnnualRate] = useState(EMBED_DEFAULTS.annualRate)
  const [installmentType, setInstallmentType] = useState<InstallmentType>('equal')

  useEffect(() => {
    document.title = t('embed.pageTitle')
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
    if (!robots) {
      robots = document.createElement('meta')
      robots.setAttribute('name', 'robots')
      document.head.appendChild(robots)
    }
    robots.setAttribute('content', 'noindex, nofollow')
    return () => {
      robots?.setAttribute('content', 'index, follow')
    }
  }, [t])

  const input: MortgageInput = useMemo(
    () => ({ propertyPrice, downPaymentPercent, loanYears, annualRate }),
    [propertyPrice, downPaymentPercent, loanYears, annualRate]
  )

  const result = useMemo(() => calculateMortgage(input, installmentType), [input, installmentType])
  const downPaymentAmount = propertyPrice * (downPaymentPercent / 100)
  const isDecreasing = installmentType === 'decreasing'

  return (
    <div className="@container min-h-screen bg-gradient-to-br from-indigo-50/40 via-white to-violet-50/40">
      <div className="max-w-2xl mx-auto p-2 @xs:p-3 @sm:p-4 @md:p-6">
        <div className="rounded-lg @sm:rounded-2xl bg-white shadow-sm border border-border p-3 @xs:p-4 @sm:p-5 @md:p-6 space-y-3 @xs:space-y-4 @sm:space-y-5">
          {/* Installment type tabs */}
          <Tabs value={installmentType} onValueChange={(v) => setInstallmentType(v as InstallmentType)}>
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="equal" className="text-xs @lg:text-sm">
                <span className="@lg:hidden">{t('installment.equalShort')}</span>
                <span className="hidden @lg:inline">{t('installment.equal')}</span>
              </TabsTrigger>
              <TabsTrigger value="decreasing" className="text-xs @lg:text-sm">
                <span className="@lg:hidden">{t('installment.decreasingShort')}</span>
                <span className="hidden @lg:inline">{t('installment.decreasing')}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Sliders */}
          <MortgageSlider
            label={t('calculator.propertyPrice')}
            value={propertyPrice}
            onChange={setPropertyPrice}
            formatValue={formatMoney}
            icon={<HouseIcon />}
            {...LIMITS.propertyPrice}
          />

          <MortgageSlider
            label={t('calculator.downPayment')}
            value={downPaymentPercent}
            onChange={setDownPaymentPercent}
            formatValue={(v) => `${v}%`}
            secondaryLabel={formatMoney(downPaymentAmount)}
            icon={<PercentIcon />}
            {...LIMITS.downPaymentPercent}
          />

          <MortgageSlider
            label={t('calculator.loanPeriod')}
            value={loanYears}
            onChange={setLoanYears}
            formatValue={(v) => `${v} ${t('calculator.years')}`}
            icon={<CalendarIcon />}
            {...LIMITS.loanYears}
          />

          <MortgageSlider
            label={t('embed.annualRate')}
            value={annualRate}
            onChange={setAnnualRate}
            formatValue={formatPercent}
            icon={<ChartIcon />}
            {...ANNUAL_RATE_LIMITS}
          />

          {/* Result: highlighted monthly payment */}
          <div className="rounded-lg @sm:rounded-xl bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white p-3 @xs:p-4 @sm:p-5">
            {isDecreasing ? (
              <div className="grid grid-cols-1 @xs:grid-cols-2 gap-2 @xs:gap-4">
                <div>
                  <p className="text-[10px] @xs:text-xs uppercase tracking-wider text-white/70">{t('results.firstPayment')}</p>
                  <p className="text-xl @xs:text-2xl @sm:text-3xl font-bold tabular-nums mt-0.5 @xs:mt-1 break-words">
                    {formatMoney(result.monthlyPayment)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] @xs:text-xs uppercase tracking-wider text-white/70">{t('results.lastPayment')}</p>
                  <p className="text-xl @xs:text-2xl @sm:text-3xl font-bold tabular-nums mt-0.5 @xs:mt-1 break-words">
                    {formatMoney(result.lastMonthlyPayment)}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-[10px] @xs:text-xs uppercase tracking-wider text-white/70">{t('results.monthlyPayment')}</p>
                <p className="text-2xl @xs:text-3xl @sm:text-4xl font-bold tabular-nums mt-0.5 @xs:mt-1 break-words">
                  {formatMoney(result.monthlyPayment)}
                </p>
              </>
            )}
          </div>

          {/* Secondary results — single column (label-left/value-right rows) up to
              ~384px, then 3-col centered grid. The 3-col layout needs more room
              than 320px once long-format currencies (EUR, USD with locale grouping)
              and multi-word labels are factored in — values clipped otherwise. */}
          <div className="grid grid-cols-1 @sm:grid-cols-3 gap-1.5 @sm:gap-2">
            <div className="rounded-lg bg-muted/40 px-3 py-2 @sm:p-3 flex items-center justify-between @sm:block @sm:text-center">
              <p className="text-[11px] text-muted-foreground">{t('results.loanAmount')}</p>
              <p className="text-sm font-bold tabular-nums @sm:mt-0.5">{formatMoney(result.loanAmount)}</p>
            </div>
            <div className="rounded-lg bg-muted/40 px-3 py-2 @sm:p-3 flex items-center justify-between @sm:block @sm:text-center">
              <p className="text-[11px] text-muted-foreground">{t('results.totalInterest')}</p>
              <p className="text-sm font-bold tabular-nums @sm:mt-0.5">{formatMoney(result.totalInterest)}</p>
            </div>
            <div className="rounded-lg bg-muted/40 px-3 py-2 @sm:p-3 flex items-center justify-between @sm:block @sm:text-center">
              <p className="text-[11px] text-muted-foreground">{t('results.totalCost')}</p>
              <p className="text-sm font-bold tabular-nums @sm:mt-0.5">{formatMoney(result.totalCost)}</p>
            </div>
          </div>

          {/* CTA — shorter text at narrow widths */}
          <a
            href={CTA_HREF}
            target="_top"
            rel="noopener"
            className="block w-full text-center rounded-lg @sm:rounded-xl bg-foreground text-background py-2.5 @xs:py-3 px-3 @xs:px-4 text-xs @xs:text-sm font-semibold hover:bg-foreground/90 transition-colors"
          >
            <span className="@lg:hidden">{t('embed.ctaShort')}</span>
            <span className="hidden @lg:inline">{t('embed.ctaFull')}</span>
          </a>
        </div>

        {/* Attribution — required for free embed use. Brand link is ALWAYS appended;
            `?attr=` only customizes the prefix text in front of it. */}
        <p className="text-center text-[11px] @xs:text-xs text-muted-foreground mt-2 @sm:mt-3">
          {customAttr || t('embed.attribution')}{' '}
          <a
            href={BRAND_HREF}
            target="_top"
            rel="noopener"
            className="font-semibold text-foreground hover:underline"
          >
            RatyGo.pl
          </a>
        </p>
      </div>
    </div>
  )
}

export default EmbedPage
